import type { JsGameLibrary } from "@/types/jsGame";

const LIB_CDN: Record<JsGameLibrary, string> = {
  kaplay: "https://unpkg.com/kaplay@3001.0.18/dist/kaplay.js",
};

interface BuildOpts {
  library: JsGameLibrary;
  code: string;
  grade: string | number;
  difficulty: string;
  theme?: string;
}

/**
 * Build the iframe srcdoc HTML. The generated code runs inside a
 * sandbox without `allow-same-origin`, behind a strict CSP, and may
 * only communicate via the LovableBridge postMessage adapter.
 */
export function buildSandboxSrcdoc({
  library,
  code,
  grade,
  difficulty,
  theme,
}: BuildOpts): string {
  const libUrl = LIB_CDN[library];
  const csp = [
    "default-src 'none'",
    "script-src 'unsafe-inline' https://cdn.jsdelivr.net https://unpkg.com",
    "style-src 'unsafe-inline' https://fonts.googleapis.com",
    "img-src data: blob:",
    "font-src data: https://fonts.gstatic.com",
    "connect-src https://cdn.jsdelivr.net https://unpkg.com https://fonts.googleapis.com https://fonts.gstatic.com",
    "frame-src 'none'",
    "object-src 'none'",
    "base-uri 'none'",
  ].join("; ");

  const ctx = JSON.stringify({
    grade: String(grade),
    difficulty,
    theme: theme ?? "classroom",
  });

  const safeCode = code.replace(/<\/script/gi, "<\\/script");

  return `<!doctype html>
<html>
<head>
<meta charset="utf-8" />
<meta http-equiv="Content-Security-Policy" content="${csp}" />
<link rel="preconnect" href="https://fonts.googleapis.com" />
<link rel="preconnect" href="https://fonts.gstatic.com" crossorigin />
<link href="https://fonts.googleapis.com/css2?family=Fredoka:wght@500;600;700&family=Nunito:wght@600;700;800&display=swap" rel="stylesheet" />
<style>
  html, body { margin: 0; padding: 0; background: #0b0f17; color: #e6edf7; font-family: 'Nunito', system-ui, sans-serif; height: 100%; overflow: hidden; }
  #game { width: 100%; height: 100%; display: flex; align-items: center; justify-content: center; position: relative; }
  #game canvas { max-width: 100%; max-height: 100%; display: block; }
  #boot { position: absolute; inset: 0; display: flex; align-items: center; justify-content: center; font-size: 14px; opacity: .7; font-family: 'Nunito', system-ui, sans-serif; }
  #controls { position: absolute; top: 10px; left: 10px; z-index: 10; display: flex; gap: 8px; }
  #controls button { font-family: 'Fredoka', system-ui, sans-serif; font-weight: 600; font-size: 14px; padding: 8px 14px; border-radius: 999px; border: 1px solid rgba(255,255,255,0.18); background: rgba(15,22,36,0.78); color: #e6edf7; cursor: pointer; backdrop-filter: blur(6px); transition: background .15s ease, transform .1s ease; }
  #controls button:hover { background: rgba(35,48,72,0.9); }
  #controls button:active { transform: scale(0.96); }
  #pauseOverlay { position: absolute; inset: 0; display: none; align-items: center; justify-content: center; background: rgba(8,12,20,0.55); z-index: 9; font-family: 'Fredoka', system-ui, sans-serif; font-weight: 700; font-size: 28px; color: #ffffff; letter-spacing: 0.5px; pointer-events: none; }
  #pauseOverlay.show { display: flex; }
  #startOverlay { position: absolute; inset: 0; display: flex; align-items: center; justify-content: center; background: rgba(8,12,20,0.65); z-index: 11; }
  #startOverlay button { font-family: 'Fredoka', system-ui, sans-serif; font-weight: 700; font-size: 22px; padding: 14px 32px; border-radius: 999px; border: none; background: linear-gradient(135deg,#78c8ff,#5aa6ff); color: #07111f; cursor: pointer; box-shadow: 0 10px 30px rgba(120,200,255,0.35); }
  #startOverlay button:hover { filter: brightness(1.05); }
</style>
</head>
<body>
<div id="boot">Loading game…</div>
<div id="game">
  <div id="controls" style="display:none;">
    <button id="pauseBtn" type="button" aria-label="Pause">⏸ Pause</button>
    <button id="restartBtn" type="button" aria-label="Restart">⟳ Restart</button>
  </div>
  <div id="pauseOverlay">Paused</div>
  <div id="startOverlay"><button id="startBtn" type="button">▶ Start Game</button></div>
</div>
<script>
(function () {
  var startedAt = Date.now();
  function post(msg) {
    try { parent.postMessage(Object.assign({ source: "lovable-game" }, msg), "*"); } catch (_) {}
  }
  window.__lovablePaused = false;
  window.LovableBridge = {
    ready: function () { post({ type: "ready" }); },
    score: function (score, max) { post({ type: "score", score: score, max: max }); },
    complete: function (payload) {
      post(Object.assign({ type: "complete", time_ms: Date.now() - startedAt }, payload || {}));
    },
    error: function (message) { post({ type: "error", message: String(message).slice(0, 500) }); },
    log: function (message, level) { post({ type: "log", message: String(message).slice(0, 500), level: level || "info" }); },
    isPaused: function () { return !!window.__lovablePaused; }
  };
  window.addEventListener("error", function (e) {
    LovableBridge.error((e && e.message) || "Unknown error");
  });
  window.addEventListener("unhandledrejection", function (e) {
    LovableBridge.error((e && e.reason && e.reason.message) || "Unhandled promise rejection");
  });
  // Prevent arrow keys / space / page-up-down from scrolling the iframe page.
  var SCROLL_KEYS = { ArrowUp:1, ArrowDown:1, ArrowLeft:1, ArrowRight:1, " ":1, Spacebar:1, PageUp:1, PageDown:1, Home:1, End:1 };
  window.addEventListener("keydown", function (e) {
    if (SCROLL_KEYS[e.key]) e.preventDefault();
  }, { passive: false });

  function loadLib(cb) {
    var s = document.createElement("script");
    s.src = ${JSON.stringify(libUrl)};
    s.onload = cb;
    s.onerror = function () { LovableBridge.error("Failed to load game library"); };
    document.head.appendChild(s);
  }

  var paused = false;
  function setPaused(p) {
    paused = !!p;
    window.__lovablePaused = paused;
    // Best-effort: pause Kaplay's debug loop if available on the active context.
    try {
      if (window.__lovableK && window.__lovableK.debug) {
        window.__lovableK.debug.paused = paused;
      }
    } catch (_) {}
    var overlay = document.getElementById("pauseOverlay");
    var btn = document.getElementById("pauseBtn");
    if (overlay) overlay.classList.toggle("show", paused);
    if (btn) btn.textContent = paused ? "▶ Resume" : "⏸ Pause";
    post({ type: paused ? "paused" : "resumed" });
  }

  function boot() {
    var bootEl = document.getElementById("boot");
    if (bootEl) bootEl.remove();
    var container = document.getElementById("game");
    try {
      if (!window.LovableGame || typeof window.LovableGame.start !== "function") {
        throw new Error("Generated game is missing LovableGame.start()");
      }
      window.LovableGame.start(Object.assign({ container: container }, ${ctx}));
      var controls = document.getElementById("controls");
      if (controls) controls.style.display = "flex";
      post({ type: "started" });
    } catch (err) {
      LovableBridge.error(err && err.message || "Game failed to start");
    }
  }

  function runGeneratedCode() {
    try {
${safeCode}
    } catch (err) {
      LovableBridge.error(err && err.message || "Generated code failed to evaluate");
    }
  }

  var libReady = false;
  function startFlow() {
    if (!libReady) return;
    var startOverlay = document.getElementById("startOverlay");
    if (startOverlay) startOverlay.remove();
    runGeneratedCode();
    boot();
  }

  var startBtn = document.getElementById("startBtn");
  if (startBtn) {
    startBtn.setAttribute("disabled", "true");
    startBtn.textContent = "Loading…";
    startBtn.addEventListener("click", startFlow);
  }
  var pauseBtn = document.getElementById("pauseBtn");
  if (pauseBtn) pauseBtn.addEventListener("click", function () { setPaused(!paused); });
  var restartBtn = document.getElementById("restartBtn");
  if (restartBtn) restartBtn.addEventListener("click", function () {
    try { if (window.LovableGame && typeof window.LovableGame.destroy === "function") window.LovableGame.destroy(); } catch (_) {}
    paused = false;
    window.__lovablePaused = false;
    var overlay = document.getElementById("pauseOverlay");
    if (overlay) overlay.classList.remove("show");
    var btn = document.getElementById("pauseBtn");
    if (btn) btn.textContent = "⏸ Pause";
    boot();
  });
  // Keyboard shortcut: P to toggle pause.
  window.addEventListener("keydown", function (e) {
    if ((e.key === "p" || e.key === "P") && !document.getElementById("startOverlay")) {
      setPaused(!paused);
    }
  });
  // Allow parent to control pause/resume too.
  window.addEventListener("message", function (e) {
    var d = e && e.data;
    if (!d || d.source !== "lovable-host") return;
    if (d.type === "pause") setPaused(true);
    else if (d.type === "resume") setPaused(false);
  });

  loadLib(function () {
    libReady = true;
    var bootEl = document.getElementById("boot");
    if (bootEl) bootEl.remove();
    if (startBtn) {
      startBtn.removeAttribute("disabled");
      startBtn.textContent = "▶ Start Game";
    }
    post({ type: "ready" });
  });
})();
</script>
</body>
</html>`;
}
